import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles, MessageCircle } from "lucide-react";
import useReveal from "../hooks/useReveal";
import WindPalm from "../components/WindPalm";
import AnimatedText from "../components/AnimatedText";
import SEO from "../components/SEO";
import { SITE, ACTIVITIES, POOL_TERRACE, PALMS } from "../mock";

const Activities = () => {
  const ref = useReveal();
  const whatsappLink = `https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
    "Hi Casa Elio! I'd like to plan some activities during my stay. Could you help arrange?"
  )}`;

  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Activities — Surf, sail, fish, ride"
        description="Things to do around San Juan del Sur Bay — surfing, zip-line canopy, sunset catamaran, sport fishing, the Christ of the Mercy lookout, and horseback rides on the beach."
        path="/activities"
      />
      {/* Page header */}
      <section className="relative pt-40 pb-12 md:pt-48 md:pb-20 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
          Activities
        </div>
        <h1 className="reveal font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-[#1f2a24] max-w-5xl">
          <span className="block"><AnimatedText text="Things to do" stagger={32} baseDelay={150} /></span>
          <span className="block opacity-80" style={{ fontStyle: "italic", fontWeight: 500 }}>
            <AnimatedText text="around the bay." stagger={30} baseDelay={550} />
          </span>
        </h1>
        <p className="reveal mt-8 max-w-2xl text-[#3a4a40] text-base md:text-lg leading-relaxed">
          From first-light surf sessions to sunset rides on the sand — every
          activity below can be arranged from the villa. Tell us what you're in
          the mood for and we'll handle the rest.
        </p>
      </section>

      {/* Activities — alternating layout */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-20 space-y-24 md:space-y-36">
        {ACTIVITIES.map((a, idx) => {
          const reverse = idx % 2 === 1;
          return (
            <div
              key={a.id}
              className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${
                reverse ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-7 [direction:ltr]">
                <div className="overflow-hidden img-hover bg-[#ebe3d2]">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="reveal-img w-full aspect-[4/3] object-cover"
                  />
                </div>
              </div>
              <div className="md:col-span-5 [direction:ltr]">
                <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-4">
                  {a.number} · {a.tag}
                </div>
                <h2 className="reveal font-serif-display text-4xl md:text-5xl text-[#1f2a24] leading-[1.05]">
                  {a.title}
                </h2>
                <p className="reveal mt-6 text-[#3a4a40] text-base md:text-[17px] leading-[1.85]">
                  {a.description}
                </p>
                <ul className="reveal mt-8 grid grid-cols-2 gap-y-5 gap-x-4">
                  <li className="flex items-start gap-3">
                    <Clock size={16} className="mt-1 text-[#2f4438]" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40]">
                        Duration
                      </div>
                      <div className="text-[14px] text-[#1f2a24]">{a.duration}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Sparkles size={16} className="mt-1 text-[#2f4438]" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40]">
                        Level
                      </div>
                      <div className="text-[14px] text-[#1f2a24]">{a.level}</div>
                    </div>
                  </li>
                </ul>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="reveal mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#1f2a24] link-underline"
                >
                  Plan this with us <ArrowRight size={14} />
                </a>
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden mt-20 md:mt-32">
        <div className="absolute inset-0 overflow-hidden">
          <img src={POOL_TERRACE} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1f2a24]/65" />
        </div>
        <WindPalm
          src={PALMS.pair}
          side="left"
          offsetX={-110}
          bottom={-50}
          width={460}
          opacity={0.85}
          silhouette
          parallax={0.14}
          scrollIntensity={1.5}
          swayDuration={9.0}
          swayPhase={-2}
          hideOnMobile
        />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36 text-center text-[#f5f0e6] z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] opacity-80 mb-5">
            Plan With Us
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-6xl leading-[1.05]">
            Let us handle the days.
          </h2>
          <p className="reveal mt-6 max-w-xl mx-auto opacity-90">
            Send us a note before you arrive and we'll line up the surf
            instructor, the boat, the horses, and the table — all you need to do
            is show up.
          </p>
          <div className="reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={SITE.airbnbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Airbnb
              <ArrowRight size={14} />
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.3em] px-7 py-4 bg-[#f5f0e6] text-[#1f2a24] hover:bg-white transition-colors"
            >
              <MessageCircle size={14} />
              Book Now
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Booking.com
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Activities;
